import React from "react";

import { Content } from "./components/layout/Content";
import Layout from "./components/layout/Layout";
import {
  Alert,
  Divider,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import CurrentStatus from "./components/CurrentStatus";
import SnapshotView from "./components/SnapshotView";
import DownTimeReport from "./components/DowntimeReport";
import ResponseTimeReport from "./components/ResponseTimeReport";
import TraceResult from "./components/TraceResult";
import { reportRepo } from "./repositories/reportRepo";

import { CheckCurrentStatus, Report, StatusResult } from "./types/types";
import { minutesToHumanReadable, queryParams } from "./utils/utils";
import Loading from "./components/commons/Loading";
import Error from "./components/commons/Error";
import DetailReport from "./components/DetailReport";
import { useQuery } from "react-query";
import CloseIcon from "@mui/icons-material/Close";

type ReportResult = {
  isLoading: boolean;
  isError: boolean;
  data?: Report;
  error: unknown;
};

const App = () => {
  const queryParamValues = queryParams();
  const { isLoading, isError, data, error, status } = useQuery(
    "repoData",
    () => reportRepo(queryParamValues),
    {
      refetchOnWindowFocus: false,
    }
  );

  const response: ReportResult = {
    isLoading,
    isError,
    data,
    error,
  };

  return (
    <Layout>
      <Content>
        <AppView report={response} />
      </Content>
    </Layout>
  );
};

type AppViewProps = {
  report: ReportResult;
};
const AppView = (props: AppViewProps) => {
  const { report } = props;
  if (report.isLoading === false && !report.error && report.data) {
    return (
      <CheckReport
        report={report.data}
        status={report.data.currentAgentStatus}
      />
    );
  } else if (report.isLoading === true) {
    return <Loading />;
  } else if (report.isError) {
    return <Error error={report.error} />;
  } else {
    return (
      <>
        <br />
        <Alert severity="info">
          This report has expired. Please logon to{" "}
          <a href="https://sonar.constellix.com">Sonar</a> to check your status.
        </Alert>
      </>
    );
  }
};

const Title = () => {
  return (
    <Typography variant="h3" gutterBottom component="div">
      Notification Report
    </Typography>
  );
};

type CheckReportProps = {
  report: Report;
  status: StatusResult;
};
const CheckReport = (props: CheckReportProps) => {
  const { report, status } = props;
  const currentStatus: CheckCurrentStatus = {
    status: report.status,
    host: report.host,
    date: new Date(report.date),
    location: report.locationLabel,
  };
  return (
    <>
      <Title />
      <CurrentStatus data={currentStatus} />
      <br />
      <Divider />
      <DetailReport data={report.reportDetail} />
      <br />
      <SnapshotView data={status} />
      <br />
      {/* <DownTimeReport data={report.downtimeBreakdown} /> */}
      <br />
      <ResponseTimeReport
        data={report.responseTime}
        location={report.locationLabel}
      />
      <br />
      <TraceResult traces={report.traces} />
      <ReportExpires expires={report.expiresInMinutes} />
    </>
  );
};

type ReportExpiresProps = {
  expires: number;
};
const ReportExpires = (props: ReportExpiresProps) => {
  const [open, setOpen] = React.useState(true);
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.5 }}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </>
      }
      message={`This report expires in ${minutesToHumanReadable(
        props.expires
      )}`}
    />
  );
};

export default App;
